import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { formatISO, startOfMonth } from 'date-fns'
import MonthStrip from '@/shared/components/MonthStrip'
import { ScrollReveal } from '@/shared/components/ui'
import { InvoiceDropZone } from '../components/InvoiceDropZone'
import { InvoicesTable } from '../components/InvoicesTable'
import { CreateInvoiceDialog } from '../components/CreateInvoiceDialog'
import { EditInvoiceDialog } from '../components/EditInvoiceDialog'
import { useBulkInvoiceQueue, BULK_INVOICE_SOFT_LIMIT } from '../hooks/useBulkInvoiceQueue'
import type { Invoice } from '../schemas/invoicesSchema'

interface EditDialogState {
  open: boolean
  invoice: Invoice | null
}

export function InvoicesPage(): ReactElement {
  const { t } = useTranslation('features-invoices')
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [monthIndex, setMonthIndex] = useState(now.getMonth())
  const [monthDirection, setMonthDirection] = useState<'forward' | 'backward' | null>(null)
  const [editDialog, setEditDialog] = useState<EditDialogState>({ open: false, invoice: null })
  const invoiceQueue = useBulkInvoiceQueue()

  const handleMonthChange = (nextYear: number, nextMonth: number): void => {
    const currentDate = new Date(year, monthIndex)
    const nextDate = new Date(nextYear, nextMonth)
    setMonthDirection(nextDate > currentDate ? 'forward' : 'backward')
    setYear(nextYear)
    setMonthIndex(nextMonth)
  }

  const handleFilesSelected = (files: File[]): void => {
    if (files.length > BULK_INVOICE_SOFT_LIMIT) {
      const ok = window.confirm(`You're about to queue ${files.length} files. Continue?`)
      if (!ok) return
    }
    void invoiceQueue.enqueue(files)
  }

  const handleEdit = (invoice: Invoice): void => {
    setEditDialog({ open: true, invoice })
  }

  const handleEditDialogChange = (open: boolean): void => {
    setEditDialog(prev => ({ open, invoice: open ? prev.invoice : null }))
  }

  const defaultSaleDate = formatISO(startOfMonth(new Date(year, monthIndex, 1)), {
    representation: 'date',
  })

  return (
    <div className="min-h-screen pt-9">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-8 space-y-6">
        <ScrollReveal blur duration={0.3}>
          <h1 className="text-2xl font-bold text-foreground">{t('title')}</h1>
        </ScrollReveal>

        <ScrollReveal delay={0.05} blur duration={0.3}>
          <MonthStrip
            year={year}
            monthIndex={monthIndex}
            onChange={handleMonthChange}
            disableFuture
          />
        </ScrollReveal>

        <ScrollReveal delay={0.1} blur duration={0.3}>
          <InvoiceDropZone onFiles={handleFilesSelected} />
        </ScrollReveal>

        <ScrollReveal delay={0.15} blur duration={0.3}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`invoices-${year}-${monthIndex}`}
              initial={{
                opacity: 0,
                x:
                  monthDirection === 'forward'
                    ? -30
                    : monthDirection === 'backward'
                      ? 30
                      : -20,
              }}
              animate={{ opacity: 1, x: 0 }}
              exit={{
                opacity: 0,
                x:
                  monthDirection === 'forward'
                    ? 30
                    : monthDirection === 'backward'
                      ? -30
                      : 20,
              }}
              transition={{ duration: 0.18, ease: 'easeInOut' }}
              className="px-6 pb-6 bg-card rounded-3xl border border-border/15"
            >
              <InvoicesTable year={year} monthIndex={monthIndex} onEdit={handleEdit} />
            </motion.div>
          </AnimatePresence>
        </ScrollReveal>
      </div>

      <CreateInvoiceDialog
        queue={invoiceQueue}
        defaultSaleDate={defaultSaleDate}
        year={year}
        monthIndex={monthIndex}
        onNavigateToMonth={handleMonthChange}
      />

      <EditInvoiceDialog
        open={editDialog.open}
        onOpenChange={handleEditDialogChange}
        invoice={editDialog.invoice}
      />
    </div>
  )
}
